<template>
  <main class="bg__color--white">
    <section
      class="banner color--white"
      :style="{ backgroundImage: `url(${translate(banner.image)})` }"
      v-if="banner"
    >
      <div class="container">
        <div class="banner__wrapper">
          <h1
            class="title__h1 banner__title margin__b--12"
            v-if="translate(banner.title)"
          >
            {{ translate(banner.title) }}
          </h1>

          <p
            class="banner__text margin__b--48"
            v-if="translate(banner.text)"
          >
            {{ translate(banner.text) }}
          </p>

          <div class="d-sm-flex align-items-center banner__actions">
            <app-link
              class="cta padding__tb--12 padding__lr--24 d-block"
              :link="banner.cta1"
            />

            <app-link
              class="link__color--white-prim link--naked bold padding__tb--12 padding__lr--24 d-block"
              :link="banner.cta2"
            />
          </div>
        </div>
      </div>
    </section>

    <section
      class="stats"
      v-if="stats?.metrics?.length"
    >
      <app-counter
        :query="stats.query"
        :metrics="stats.metrics"
      />
    </section>

    <section
      class="article"
      v-if="about"
    >
      <div class="container article__wrapper">
        <div>
          <h2
            class="title__h2"
            v-if="translate(about.title)"
          >
            {{ translate(about.title) }}
          </h2>

          <div
            class="editor editor__content article__text"
            v-html="translate(about.text)"
            v-if="translate(about.text)"
          />

          <app-link
            :link="about.cta"
            class="cta padding__tb--12 padding__lr--24"
          />
        </div>

        <figure class="article__figure">
          <img
            class="article__image"
            :src="translate(about.image)"
            v-if="translate(about.image)"
          >

          <fa-icon
            class="no-thumb"
            :icon="['far', 'image']"
            v-else
          />
        </figure>
      </div>
    </section>

    <section
      class="projects align--center"
      v-if="actions && projects.length"
    >
      <div class="container">
        <h2
          class="title__h2 margin__b--36"
          v-if="translate(actions.title)"
        >
          {{ translate(actions.title) }}
        </h2>

        <div class="row align--left">
          <template
            v-for="project in projects"
            :key="project.id"
          >
            <section class="col-12 col-sm-6 col-md-3">
              <project-card
                class="project-card"
                :project="project"
              />
            </section>
          </template>
        </div>

        <app-link
          :link="actions.cta"
          class="cta padding__tb--12 padding__lr--24 margin__t--36"
        />
      </div>
    </section>

    <section
      class="article article--more"
      v-if="media_text"
    >
      <div class="container article__wrapper article__wrapper--reverse">
        <figure class="article__figure">
          <img
            class="article__image"
            :src="translate(media_text.image)"
            v-if="translate(media_text.image)"
          >

          <fa-icon
            class="no-thumb"
            :icon="['far', 'image']"
            v-else
          />
        </figure>

        <div>
          <h2
            class="title__h2"
            v-if="translate(media_text.title)"
          >
            {{ translate(media_text.title) }}
          </h2>

          <div
            class="editor editor__content article__text"
            v-html="translate(media_text.text)"
            v-if="translate(media_text.text)"
          />

          <app-link
            :link="media_text.cta"
            class="cta padding__tb--12 padding__lr--24"
          />
        </div>
      </div>
    </section>
  </main>
</template>

<script setup>
import { defineAsyncComponent } from 'vue'
import { useProjectStore } from '@/stores/ProjectStore'
import { useI18n } from '@/vendors/i18n'
import { get as _get } from 'lodash'

import ProjectCard from '&/organisms/ProjectCard'
const AppLink = defineAsyncComponent(() => import('&/atoms/AppLink'))
const AppCounter = defineAsyncComponent(() => import('&/molecules/AppCounter'))

const { sections } = defineProps({
  page: { type: Object, required: true },
  sections: { type: Object, required: true }
})

const { translate } = useI18n()
const { getProjects } = useProjectStore()

const { banner = {}, stats = {}, about = {}, actions = {}, media_text = {} } = sections.attributes

const { projects } = getProjects({ query: _get(actions, 'query', { 'sort_by': 'activity_last', 'per_page': 4 }) })
</script>

<style lang="scss" scoped>
$color--primary: #adab00;
$color--secondary: #625731;
$color--tertiary: #438382;

:deep(.cta) {
  background-color: $color--primary;
  border-radius: $radius__buttons;
  color: $white;
  cursor: pointer;
  display: inline-block;
  font-weight: normal;
  position: relative;
  transition: all $transition__duration;

  &:hover,
  &:focus {
    background-color: #797700;
  }
}

.no-thumb {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  color: $white;
  font-size: rem(72px);
}

.title {
  &__h1 {
    font-family: $font-family--secondary;
    font-size: rem(32px);
    line-height: 1.4;

    @include mq(sm) {
      font-size: rem(47px);
    }
  }

  &__h2 {
    color: $color--secondary;
    font-size: rem(28px);
    font-weight: 900;
    line-height: 1.2;

    @include mq(md) {
      font-size: rem(36px);
    }
  }
}

.banner {
  position: relative;
  padding: 48px 0;
  text-align: center;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;

  @include mq(sm) {
    padding: 108px 0 168px;
    text-align: left;
  }

  @include mq(lg) {
    padding: 108px 0 168px;
  }

  &::before {
    content: '';
    display: block;
    background-color: rgba($color--secondary, .2);
    height: 100%;
    left: 0;
    top: 0;
    position: absolute;
    width: 100%;
    z-index: 0;

    @include mq(sm) {
      display: none;
    }
  }

  &__wrapper {
    position: relative;
    max-width: 450px;
    z-index: 10;
    margin: 0 auto;

    @include mq(sm) {
      margin: 0;
    }
  }

  &__title {
    text-shadow: 1px 1px 2px rgba($black, .8);
  }

  &__text {
    text-shadow: 1px 1px 2px rgba($black, .8);
    font-size: rem(20px);
    line-height: 1.4;

    @include mq(sm) {
      font-size: rem(27px);
    }
  }

  &__actions {
    font-size: 1.2rem;
  }
}

.stats {
  font-size: rem(18px);
  color: $white;

  :deep() {
    .counter {
      &__ul {
        position: relative;
        display: flex;
        flex-direction: column;
        justify-content: space-around;
        align-items: center;
        background-color: $color--tertiary;
        padding: 24px;

        @include mq(sm) {
          border-radius: $radius__cards;
          flex-direction: row;
          padding: 36px;
          margin: -70px auto 0;
          max-width: 850px;
          width: 90%;
        }
      }

      &__li {
        text-align: center;

        &:not(:last-of-type) {
          margin-bottom: 24px;

          @include mq(sm) {
            margin-bottom: 0;
          }
        }

        @include mq(sm) {
          width: 33%;
        }
      }

      &__value {
        display: block;
        font-size: 2em;
        line-height: 1;
        font-weight: bold;
        margin-bottom: 6px;
      }
    }
  }
}

.article {
  &--more {
    background-color: #ecece8;
  }

  &__wrapper {
    display: grid;
    grid-template-columns: 1fr;
    align-items: center;
    gap: 24px;
    padding-block: 36px;

    @include mq(sm) {
      grid-template-columns: repeat(2, 1fr);
      padding-block: 24px;
    }

    @include mq(md) {
      padding-block: 48px;
      gap: 70px;
    }

    @include mq(lg) {
      padding-block: 72px;
      grid-template-columns: auto minmax(545px, 1fr);
    }

    &--reverse {
      @include mq(lg) {
        grid-template-columns: minmax(545px, 1fr) auto;
      }
    }
  }

  &__text {
    color: $color--secondary;
    font-size: rem(18px);
    line-height: 1.4;
    margin: 24px 0;

    & p:not(:last-of-type) {
      margin-bottom: 36px;
    }
  }

  &__figure {
    background: $color--tertiary;

    @include aspect-ratio(545, 364);
  }

  &__image {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: auto;
  }
}

.projects {
  padding-block: 36px;

  @include mq(sm) {
    grid-template-columns: repeat(2, 1fr);
    padding-block: 24px;
  }

  @include mq(md) {
    grid-template-columns: auto minmax(545px, 1fr);
    padding-block: 48px;
    gap: 70px;
  }

  @include mq(lg) {
    padding-block: 72px;
  }
}
</style>
